const isStringifiedJSON = (obj)=>{
    if (typeof obj === 'object') {
        return false;
    }
    if (obj === 'true' || obj === 'false') {
        return false;
    }
    try {
        const val = JSON.parse(obj);
        const isNumber = typeof val === 'number';
        return !isNumber && val !== null;
    } catch (unused) {
        return false;
    }
};
const getValueForString = (obj)=>{
    if (isStringifiedJSON(obj)) {
        return JSON.parse(obj);
    } else {
        return obj;
    }
};
/**
 * The function converts an optionsOject [key]:value to value/label array of objects.
 * @param optionsObject {Record<string, string>}
 * @returns {Option[]}
 */ const convertRecordToOptionList = (optionsObject)=>{
    return Object.entries(optionsObject).map(([value, label])=>({
            value,
            label
        }));
};
/**
 * The function converts an object list to value/label array of objects.
 * @param optionsObject {T[]}
 * @returns {Option[]}
 */ const convertObjecListToOptionList = (optionsObject, keyField, labelField)=>{
    return optionsObject.map((item)=>({
            value: item[keyField],
            label: item[labelField]
        }));
};
// ----------------------------------
// lodash style functions
// ----------------------------------
const get = (obj, path, defaultValue = undefined)=>{
    const travel = (regexp)=>String.prototype.split.call(path, regexp).filter(Boolean).reduce((res, key)=>res !== null && res !== undefined ? res[key] : res, obj);
    const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
    return result === undefined || result === obj ? defaultValue : result;
};
/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 *
 * @param {*} value - The value to compare.
 * @param {*} other - The other value to compare.
 * @returns {boolean} - Returns `true` if the values are equivalent, else `false`.
 */ const isEqual = (x, y)=>{
    const ok = Object.keys, tx = typeof x, ty = typeof y;
    return x && y && tx === 'object' && tx === ty ? ok(x).length === ok(y).length && ok(x).every((key)=>isEqual(x[key], y[key])) : x === y;
};
/**
 * Checks if value is an empty object or collection or string.
 *
 * @param {*} value - The value to compare.
 * @returns {boolean} - Returns `true` if the value is an empty object or collection, else `false`.
 */ const isEmpty = (value)=>{
    return value == null || typeof value === 'object' && Object.keys(value).length === 0 || typeof value === 'string' && value.trim().length === 0;
};
/**
 * Removes duplicate values from an array.
 *
 * @param {Array} arr - The array to inspect.
 * @returns {Array} - Returns the new duplicate-free array.
 */ const uniq = (arr)=>{
    return [
        ...new Set(arr)
    ];
};
/**
 * Removes duplicate values from an array except that it accepts iteratee which is invoked for each element in array to generate the criterion by which uniqueness is computed.
 *
 * @param {Array} arr - The array to inspect.
 * @param {Function} fnBy - The iteratee invoked per element.
 * @returns {Array} - Returns the new duplicate-free array.
 */ const uniqBy = (arr, fnBy)=>{
    return arr.filter((el, index)=>{
        return arr.findIndex((step)=>{
            return fnBy ? typeof fnBy === 'function' ? fnBy(el) === fnBy(step) : el[fnBy] === step[fnBy] : el === step;
        }) === index;
    });
};
/**
 * Similar to uniq except that it accepts a comparator which is invoked to compare elements of array
 *
 * @param {Array} arr - The value to check.
 * @param {function} fn - the comparator function
 * @returns {Array} - Returns the new duplicate free array.
 */ const uniqWith = (arr, fn)=>{
    return arr.filter((el, index)=>{
        return arr.findIndex((step)=>{
            return fn ? fn(el, step) : el[fn] === step[fn];
        }) === index;
    });
};
/**
 * Checks if `value` is classified as a string primitive.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if `value` is a string, else `false`.
 */ const isString = (str)=>{
    if (str != null && typeof str.valueOf() === 'string') {
        return true;
    }
    return false;
};
/**
 * Checks if `value` is classified as a number primitive or object.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if `value` is a number, else `false`.
 */ const isNumber = (num)=>{
    if (num != null && typeof num.valueOf() === 'number') {
        return true;
    }
    return false;
};
/**
 * Converts the first character of string to upper case and the remaining to lower case.
 *
 * @param {string} string - The string to capitalize.
 * @returns {string} - Returns the capitalized string.
 */ const capitalize = (str)=>{
    var _str_charAt, _str_slice;
    return str ? (str === null || str === void 0 ? void 0 : (_str_charAt = str.charAt(0)) === null || _str_charAt === void 0 ? void 0 : _str_charAt.toUpperCase()) + (str === null || str === void 0 ? void 0 : (_str_slice = str.slice(1)) === null || _str_slice === void 0 ? void 0 : _str_slice.toLowerCase()) : '';
};
/**
 * Converts the first character of string to upper case.
 *
 * @param {string} string - The string to convert.
 * @returns {string} - Returns the converted string.
 */ const upperFirst = (string)=>{
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
};
/**
 * Converts string to start case.
 *
 * @param {string | undefined} str - The string to modify.
 * @returns {string} - Returns string converted to startCase.
 */ const startCase = (str)=>{
    return str ? str.replace(/([a-zA-Z0-9]+(?:['’][a-zA-Z0-9]+)?)|([a-zA-Z]+)/g, (match)=>{
        return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
    }).replace(/[_-]+/g, ' ') // Replace underscores or hyphens with spaces
    .trim() : '';
};
/**
 * Truncates string if it's longer than the given maximum string length. The last characters of the truncated string are replaced with the omission string which defaults to "...".
 *
 * @param {string} str - The string to truncate.
 * @param {number} length - The maximum string length.
 * @param {string} separator - The separator pattern to truncate to.
 * @returns {string} - Returns the truncated string.
 */ const truncate = (str, length, separator = '...')=>{
    if (!str || length <= 0) {
        return '';
    }
    if (str.length <= length) {
        return str;
    }
    // Max length is length minus the length of omission ...
    const maxLength = length - separator.length;
    if (separator) {
        const lastIndex = str.lastIndexOf(separator, maxLength);
        if (lastIndex !== -1) {
            return str.slice(0, lastIndex) + separator;
        }
    }
    return str.slice(0, maxLength) + separator;
};
/**
 * This method is like _.union except that it accepts iteratee which is invoked for each element of each arrays to generate the criterion by which uniqueness is computed.
 *
 * @param {...Array} arrays - The arrays to inspect.
 * @param {Function} iteratee - The iteratee invoked per element.
 * @returns {string} - Returns the new array of combined values.
 */ const unionBy = (...arrays)=>{
    const iteratee = arrays.pop();
    if (Array.isArray(iteratee)) {
        return [];
    }
    return [
        ...arrays
    ].flat().filter(((set)=>(o)=>set.has(iteratee(o)) ? false : set.add(iteratee(o)))(new Set()));
};
/**
 * Creates an object composed of the picked object properties.
 *
 * @param {Object} object - The source object.
 * @param {string|string[]} keys - The property paths to pick..
 * @returns {string} - Returns the new object.
 */ const pick = (object, keys)=>{
    return keys.reduce((obj, key)=>{
        if (object && object.hasOwnProperty(key)) {
            obj[key] = object[key];
        }
        return obj;
    }, {});
};
/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist, it's created.
 *
 * @param {Object} object - The object to modify.
 * @param {Array|string} path - The path of the property to set.
 * @param {*} value - The value to set.
 * @returns {Object} - Returns `object`.
 */ const set = (obj, path, value)=>{
    if (obj == null) {
        return obj;
    }
    const keys = Array.isArray(path) ? path : parsePath(path);
    let current = obj;
    keys.forEach((key, index)=>{
        if (index === keys.length - 1) {
            current[key] = value;
        } else {
            // key does not exist, create
            if (!current[key]) {
                current[key] = {};
            }
            current = current[key];
        }
    });
    return obj;
};
// helper function to get a value from an object using a dot notation string
const getValue = (obj, path)=>{
    return path.split('.').reduce((acc, key)=>acc ? acc[key] : undefined, obj);
};
/**
 * Creates an object composed of keys generated from the results of running each element of collection through
 * iteratee. The corresponding value of each key is an array of the elements responsible for generating the
 * key.
 *
 * @param collection The collection to iterate over.
 * @param iteratee The iteratee to transform keys.
 * @return Returns the composed aggregate object.
 */ const groupBy = (collection, iteratee)=>{
    const result = {};
    collection === null || collection === void 0 ? void 0 : collection.forEach((item)=>{
        // Determine the key using either a function, a property name, or a deep path
        let key;
        if (typeof iteratee === 'function') {
            key = iteratee(item);
        } else if (typeof iteratee === 'string') {
            var _getValue;
            key = (_getValue = getValue(item, iteratee)) !== null && _getValue !== void 0 ? _getValue : '';
        } else {
            key = item[iteratee];
        }
        const keyString = String(key);
        if (!result[keyString]) {
            result[keyString] = [];
        }
        result[keyString].push(item);
    });
    return result;
};
// a helper function to parse paths and correctly interpret array indices in addition to dot notation.
const parsePath = (path)=>{
    const regex = /(\w+|\d+)|\[(\d+)\]/g;
    const keys = [];
    let match;
    while((match = regex.exec(path)) !== null){
        keys.push(match[1] || match[2] ? match[1] || match[2] : match[0]);
    }
    return keys;
};
/**
 * Checks if path is a direct property of object.
 *
 * @param {Object} obj - The object to query.
 * @param {string} key - The path to check.
 * @returns {boolean} - Returns true if path exists, else false.
 */ const has = (obj, path)=>{
    if (obj == null) {
        return false;
    }
    const keys = Array.isArray(path) ? path : parsePath(path);
    let current = obj;
    for(let i = 0; i < keys.length; i++){
        const key = keys[i];
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
            return false;
        }
        current = current[key];
    }
    return true;
};
/**
 * Creates a function that performs a partial deep comparison between a given object and source, returning true if the given object has equivalent property values, else false.
 *
 * @param {*} source - The object of property values to match.
 * @returns {function} - Returns the new spec function.
 */ const matches = (source)=>{
    return function(obj) {
        for(const key in source){
            if (source.hasOwnProperty(key)) {
                if (obj[key] !== source[key]) {
                    return false;
                }
            }
        }
        return true;
    };
};
/**
 * Performs a partial deep comparison between `object` and `source` to determine if `object` contains equivalent property values.
 *
 * @param {Object} object - The object to inspect.
 * @param {Object} source - The object of property values to match.
 * @returns {boolean} - Returns `true` if `object` is a match, else `false`.
 */ const isMatch = (object, source)=>{
    for(let key in source){
        if (Array.isArray(source[key]) && Array.isArray(object[key])) {
            return source[key].every((v)=>object[key].includes(v));
        }
        return typeof source[key] === 'object' ? isMatch(object[key], source[key]) : object[key] === source[key];
    }
    return JSON.stringify(object) === JSON.stringify(source);
};
/**
 * This method is like xor except that it accepts comparator which is invoked to compare elements of arrays.
 *
 * @param {Array} arr1 - The first array to inspect.
 * @param {Array} arr2 - The second array to inspect.
 * @param {function} comparator - The comparator invoked per element.
 * @returns {Array} - Returns the new array of filtered values.
 */ const xorWith = (arr1, arr2, comparator)=>{
    let result = [];
    const combined = [
        ...arr1,
        ...arr2
    ];
    combined.forEach((item)=>{
        const foundInArr1 = arr1.some((a)=>comparator(a, item));
        const foundInArr2 = arr2.some((b)=>comparator(b, item));
        if (foundInArr1 !== foundInArr2) {
            result.push(item);
        }
    });
    return result;
};
/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} leading - Specify invoking on the leading edge of the timeout.
 * @return {Function} - Returns the new debounced function.
 */ const debounce = (func, wait, leading = false)=>{
    let timeout;
    return function(...args) {
        clearTimeout(timeout);
        if (leading && !timeout) {
            func.apply(this, args);
        }
        timeout = setTimeout(()=>{
            timeout = null;
            if (!leading) {
                func.apply(this, args);
            }
        }, wait);
    };
};
/**
 * Recursively clones value.
 *
 * @param {*} value - The value to recursively clone.
 * @returns {*} - Returns the deep cloned value.
 */ const cloneDeep = (value)=>{
    // Check for null or undefined values
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (value instanceof Date) {
        return new Date(value.getTime());
    }
    if (value instanceof RegExp) {
        return new RegExp(value);
    }
    if (Array.isArray(value)) {
        return value.map((item)=>cloneDeep(item)); // Recursively clone each item in the array
    }
    const clonedObject = {};
    for(const key in value){
        if (value.hasOwnProperty(key)) {
            clonedObject[key] = cloneDeep(value[key]); // Recursively clone each property
        }
    }
    return clonedObject;
};

var objectUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  capitalize: capitalize,
  cloneDeep: cloneDeep,
  convertObjecListToOptionList: convertObjecListToOptionList,
  convertRecordToOptionList: convertRecordToOptionList,
  debounce: debounce,
  get: get,
  getValueForString: getValueForString,
  groupBy: groupBy,
  has: has,
  isEmpty: isEmpty,
  isEqual: isEqual,
  isMatch: isMatch,
  isNumber: isNumber,
  isString: isString,
  isStringifiedJSON: isStringifiedJSON,
  matches: matches,
  pick: pick,
  set: set,
  startCase: startCase,
  truncate: truncate,
  unionBy: unionBy,
  uniq: uniq,
  uniqBy: uniqBy,
  uniqWith: uniqWith,
  upperFirst: upperFirst,
  xorWith: xorWith
});

export { xorWith as A, upperFirst as a, get as b, cloneDeep as c, uniqWith as d, unionBy as e, isEmpty as f, groupBy as g, has as h, isEqual as i, convertRecordToOptionList as j, capitalize as k, isStringifiedJSON as l, matches as m, getValueForString as n, objectUtils as o, pick as p, isString as q, convertObjecListToOptionList as r, set as s, truncate as t, uniqBy as u, debounce as v, isMatch as w, isNumber as x, startCase as y, uniq as z };
//# sourceMappingURL=object-utils-CWxdM56M.mjs.map
